import {
  ColorConvertible,
  Font,
  GroupNodeStyle,
  IconLabelStyle,
  InteriorNodeLabelModel,
  LabelStyle,
  StretchNodeLabelModel
} from '@yfiles/yfiles';
import { HEAT_MAP, NODE, TEXT, UI } from './colors.ts';
import { fontFamily } from './styles.ts';

import appIcon from '../assets/icons/flow/application.svg';
import teamIcon from '../assets/icons/flow/team.svg';
import agentIcon from '../assets/icons/flow/agent.svg';
import capabilityIcon from '../assets/icons/flow/note.svg';
import alarmIcon from '../assets/icons/red-circle-svgrepo-com.svg';

export type HeatMapColor = 'GOOD' | 'WARNING' | 'BAD';

export const heatMapFills: Record<HeatMapColor, ColorConvertible> = {
  GOOD: HEAT_MAP.FILL.GOOD,     // Light green background
  WARNING: HEAT_MAP.FILL.WARNING, // Light yellow background
  BAD: HEAT_MAP.FILL.BAD        // Light red background
};

export const heatMapStrokes: Record<HeatMapColor, ColorConvertible> = {
  GOOD: HEAT_MAP.STROKE.GOOD,     // Green border
  WARNING: HEAT_MAP.STROKE.WARNING, // Yellow border
  BAD: HEAT_MAP.STROKE.BAD        // Red border
};

export const heatMapLabelFill: Record<HeatMapColor, ColorConvertible> = {
  GOOD: HEAT_MAP.TEXT.GOOD,     // Green text
  WARNING: HEAT_MAP.TEXT.WARNING, // Yellow text
  BAD: HEAT_MAP.TEXT.BAD        // Red text
};

export function getApplicationGroupNodeStyle(heatMapColor: HeatMapColor | 'NONE' = 'NONE', performanceMetric: string = 'Health') {
  // Only apply heatmapping if not using Health or Time metric
  if (performanceMetric !== 'Health' && performanceMetric !== 'Time' && heatMapColor && heatMapColor !== 'NONE') {
    return getGroupNodeStyle(heatMapFills[heatMapColor], heatMapStrokes[heatMapColor]);
  }
  return getGroupNodeStyle(NODE.FILL.DEFAULT, NODE.STROKE.APPLICATION);
}

export function getTeamGroupNodeStyle(heatMapColor: HeatMapColor | 'NONE' = 'NONE', performanceMetric: string = 'Health') {
  // Only apply heatmapping if not using Health or Time metric
  if (performanceMetric !== 'Health' && performanceMetric !== 'Time' && heatMapColor && heatMapColor !== 'NONE') {
    return getGroupNodeStyle(heatMapFills[heatMapColor], heatMapStrokes[heatMapColor], true);
  }
  return getGroupNodeStyle(NODE.FILL.TEAM, NODE.STROKE.TEAM, true);
}

export function getCapabilityGroupNodeStyle(heatMapColor: HeatMapColor | 'NONE' = 'NONE', performanceMetric: string = 'Health') {
  // Only apply heatmapping if not using Health or Time metric
  if (performanceMetric !== 'Health' && performanceMetric !== 'Time' && heatMapColor && heatMapColor !== 'NONE') {
    return getGroupNodeStyle(heatMapFills[heatMapColor], heatMapStrokes[heatMapColor]);
  }
  return getGroupNodeStyle(NODE.FILL.CAPABILITY, NODE.STROKE.CAPABILITY);
}

export function getGroupNodeStyle(
  fill: ColorConvertible,
  stroke: ColorConvertible,
  dashed: boolean = false
) {
  return new GroupNodeStyle({
    // Tab appearance
    tabFill: fill,
    tabPosition: 'top',
    tabHeight: 80,
    tabWidth: 200,
    tabSlope: 10,

    // Content area appearance
    contentAreaFill: fill,
    contentAreaPadding: 18, // Padding inside the content area

    // Icons - set foreground color to dark for better visibility
    folderIcon: 'chevron-right',
    groupIcon: 'chevron-down',
    iconBackgroundFill: fill,
    iconForegroundFill: UI.ICON.FOREGROUND, // Dark color for the icons
    iconSize: 20,
    iconOffset: 5,

    // Visual effects
    cornerRadius: 4,

    stroke: `1px ${dashed ? 'dashed' : ''} ${stroke}`,

    // CSS class for additional styling
    cssClass: 'group-node-style'
  });
}

export const applicationGroupNodeIconLabelStyle = new IconLabelStyle({
  href: appIcon,
  iconSize: [24, 24],
  iconPlacement: InteriorNodeLabelModel.LEFT,
  autoFlip: true,
  wrappedStyle: new LabelStyle({
    verticalTextAlignment: 'center',
    horizontalTextAlignment: 'left',
    font: new Font({
      fontFamily,
      fontSize: 12,
      fontWeight: '400'
    }),
    autoFlip: false,
    wrapping: 'wrap-word-ellipsis',
    textFill: '#605E5C',
    padding: { top: 0, left: 34, right: 0, bottom: 0 }
  })
});

export const teamGroupNodeIconLabelStyle = applicationGroupNodeIconLabelStyle.clone();
teamGroupNodeIconLabelStyle.href = teamIcon;

export const agentGroupNodeIconLabelStyle = applicationGroupNodeIconLabelStyle.clone();
agentGroupNodeIconLabelStyle.href = agentIcon;

export const alarmIconLabelStyle = new IconLabelStyle({
  href: alarmIcon,
  iconSize: [8, 8],
  iconPlacement: InteriorNodeLabelModel.LEFT,
  cssClass: 'alarm-icon',
  autoFlip: true,
  wrappedStyle: new LabelStyle({
    verticalTextAlignment: 'center',
    horizontalTextAlignment: 'left',
    font: new Font({
      fontFamily,
      fontSize: 12,
      fontWeight: '400'
    }),
    autoFlip: false,
    wrapping: 'wrap-word-ellipsis',
    padding: { top: 0, left: 14, right: 0, bottom: 0 }
  })
});

export const capabilityGroupNodeIconLabelStyle = applicationGroupNodeIconLabelStyle.clone();
capabilityGroupNodeIconLabelStyle.href = capabilityIcon;

export const groupNodeIconLabelLayoutParameter = new InteriorNodeLabelModel({
  padding: { top: 12, left: 7, right: 0, bottom: 0 }
}).createParameter('top-left');

export const groupNodeTitleLabelStyle = new LabelStyle({
  verticalTextAlignment: 'center',
  horizontalTextAlignment: 'left',
  font: new Font({
    fontFamily,
    fontSize: 14,
    fontWeight: '600'
  }),
  shape: 'round-rectangle',
  wrapping: 'wrap-word-ellipsis',
  textFill: TEXT.DARK
});

export const groupNodeTitleLabelLayoutParameter = new StretchNodeLabelModel({
  padding: { top: 42, left: 9, right: 9, bottom: 0 }
}).createParameter('top');

export const groupNodePercentageLabelStyle = new LabelStyle({
  verticalTextAlignment: 'center',
  horizontalTextAlignment: 'left',
  font: new Font({
    fontFamily,
    fontSize: 12,
    fontWeight: '400'
  }),
  shape: 'round-rectangle',
  wrapping: 'wrap-word-ellipsis',
  backgroundFill: TEXT.MEDIUM,
  textFill: TEXT.LIGHT,
  padding: { top: 4, left: 4, right: 4, bottom: 4 },
});

export const groupNodePercentageLabelLayoutParameter = new InteriorNodeLabelModel({
  padding: { top: 8, left: 0, right: 35, bottom: 0 }
}).createParameter('top-right');
